import { Checkbox, Col, DatePicker, Input, Row, Select, Skeleton, Modal, message } from "antd";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { CheckCircleFilled } from "@ant-design/icons";
import DashboardStyle from "./style";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DonutChart from "../../Reusable/Charts/DountChart";
import UseApi from "../../Reusable/CallApi/UseApi";
// Same plain pencil/trash icons Certificate.js uses; the mint badge behind
// them on hover comes from .todo-action-btn in style.js.
import { ReactComponent as EditPencil } from "../../../assets/Images/Edit_Pencil_01.svg";
import { ReactComponent as TrashFull } from "../../../assets/Images/Trash_Full.svg";

const { Option } = Select;

const LEARNER_PROGRESS_COLORS = {
  Completed: "#01FF01",
  "In Progress": "#FF943B",
  "Not Started": "#5A70FF",
  "Not Passed": "#FF3F7C",
};

const ordinalSuffix = (day) =>
  day % 10 === 1 && day !== 11
    ? "st"
    : day % 10 === 2 && day !== 12
    ? "nd"
    : day % 10 === 3 && day !== 13
    ? "rd"
    : "th";

// "05th August, 2024" - used by the due-date picker in the Create To-dos modal.
const formatOrdinalDate = (date) => {
  const day = date.date();
  return `${String(day).padStart(2, "0")}${ordinalSuffix(day)} ${date.format("MMMM, YYYY")}`;
};

// "15th Aug 2024" - used for each to-do row's due date.
const formatShortOrdinalDate = (value) => {
  const date = dayjs(value);
  if (!date.isValid()) return "";
  const day = date.date();
  return `${day}${ordinalSuffix(day)} ${date.format("MMM YYYY")}`;
};

// "3rd Sep, 2026; 04:30 PM" - used for Recent Activity entries.
const formatActivityDate = (value) => {
  const date = dayjs(value);
  if (!date.isValid()) return "";
  const day = date.date();
  return `${day}${ordinalSuffix(day)} ${date.format("MMM, YYYY; hh:mm A")}`;
};

const Dashboard = () => {
  const initialLoader = useSelector(
    (state) => state.systemTracking.initialLoader
  );
  const clientId = useSelector(
    (state) => state.authTracking.userInfo?.clientId
  );
  const navigate = useNavigate();

  const { request: fetchDashboard } = UseApi();
  const { request: todoRequest } = UseApi();
  const { request: fetchCourses } = UseApi();

  const [overview, setOverview] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [learnerProgress, setLearnerProgress] = useState([]);
  const [todos, setTodos] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(undefined);

  const loadDashboard = (courseId) => {
    if (!clientId) return;
    const endPoint = courseId
      ? `/dashboard/getdashboard/${clientId}?course_id=${courseId}`
      : `/dashboard/getdashboard/${clientId}`;

    fetchDashboard({ endPoint })
      .then((res) => {
        const data = res?.response || {};
        setOverview(data);
        setRecentActivity(data.recent_activity || []);
        setLearnerProgress(
          (data.learner_progress || []).map((item) => ({
            ...item,
            color: LEARNER_PROGRESS_COLORS[item.type] || "#d9d9d9",
          }))
        );
      })
      .catch(() => message.error("Failed to load dashboard data"));
  };

  useEffect(() => {
    if (!clientId) return;

    loadDashboard(selectedCourseId);

    todoRequest({ endPoint: `/dashboard/gettodolist/${clientId}` })
      .then((res) => setTodos(res?.response || []))
      .catch(() => message.error("Failed to load to-dos"));

    // The filter dropdown needs real courses, not a hardcoded label list -
    // it was previously offering three made-up course names that couldn't
    // ever match a real course_id, so picking one could never do anything.
    fetchCourses({ endPoint: `/course/getcourse/${clientId}` })
      .then((res) => setCourseOptions(res?.response || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleCourseFilterChange = (courseId) => {
    setSelectedCourseId(courseId);
    loadDashboard(courseId);
  };

  // Checking a to-do off marks it complete via PATCH - it stays in the
  // list (just visually struck through) instead of disappearing, and the
  // completed flag is persisted on tymepluhrlmstodolist.completed so it's
  // still checked after navigating away and back or reloading, not just
  // held in local state that resets on unmount.
  const handleToggleComplete = (item) => {
    const nextCompleted = !item.completed;
    setTodos((prev) =>
      prev.map((t) => (t.task_id === item.task_id ? { ...t, completed: nextCompleted } : t))
    );
    todoRequest({
      endPoint: `/dashboard/updatetodolist/${clientId}/${item.task_id}`,
      method: "patch",
      data: {
        task_name: item.task_name,
        due_date: item.due_date,
        created_at: item.created_at,
        completed: nextCompleted,
      },
    }).catch(() => {
      message.error("Failed to update to-do");
      setTodos((prev) =>
        prev.map((t) => (t.task_id === item.task_id ? { ...t, completed: item.completed } : t))
      );
    });
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    if (!showDeleted) return;
    const timer = setTimeout(() => setShowDeleted(false), 1500);
    return () => clearTimeout(timer);
  }, [showDeleted]);

  const confirmDeleteTodo = () => {
    const taskId = deleteTarget;
    setDeleteTarget(null);
    todoRequest({
      endPoint: `/dashboard/deletetodolist/${clientId}/${taskId}`,
      method: "delete",
    })
      .then(() => {
        setTodos((prev) => prev.filter((item) => item.task_id !== taskId));
        setShowDeleted(true);
      })
      .catch(() => message.error("Failed to delete to-do"));
  };

  const [editingTodo, setEditingTodo] = useState(null);
  const [editText, setEditText] = useState("");
  const [editDueDate, setEditDueDate] = useState(dayjs());

  const openEditTodo = (item) => {
    setEditingTodo(item);
    setEditText(item.task_name);
    setEditDueDate(dayjs(item.due_date));
  };

  const closeEditTodo = () => setEditingTodo(null);

  const handleSaveEditTodo = () => {
    const trimmed = editText.trim();
    if (!trimmed) {
      message.error("To-do text can't be empty");
      return;
    }
    todoRequest({
      endPoint: `/dashboard/updatetodolist/${clientId}/${editingTodo.task_id}`,
      method: "patch",
      data: {
        task_name: trimmed,
        due_date: editDueDate.format("YYYY-MM-DD"),
        created_at: editingTodo.created_at,
      },
    })
      .then((res) => {
        const updated = res?.response?.[0];
        message.success("To-do updated");
        setTodos((prev) =>
          prev.map((item) =>
            item.task_id === editingTodo.task_id ? { ...item, ...updated } : item
          )
        );
        setEditingTodo(null);
      })
      .catch(() => message.error("Failed to update to-do"));
  };

  const [open, setOpen] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [newItemText, setNewItemText] = useState("");
  const [dueDate, setDueDate] = useState(dayjs());

  const showModal = () => setOpen(true);

  const handleCancel = () => {
    setOpen(false);
    setChecklist([]);
    setNewItemText("");
  };

  const handleCreate = () => {
    // A to-do only lands in `checklist` once Enter is pressed in the input
    // (see addChecklistItem). Typing one and clicking Create straight away -
    // the natural thing to do - left checklist empty and always hit the
    // "add at least one" error below, even though text was clearly typed.
    // Folding in whatever's still sitting in the input fixes that without
    // requiring Enter first.
    const pendingText = newItemText.trim();
    const finalChecklist = pendingText
      ? [...checklist, { key: checklist.length, label: pendingText, checked: false }]
      : checklist;

    if (finalChecklist.length === 0) {
      message.error("Add at least one to-do first");
      return;
    }
    todoRequest({
      endPoint: `/dashboard/addtodolist/${clientId}`,
      method: "post",
      data: {
        task_name: finalChecklist.map((item) => item.label),
        due_date: dueDate.format("YYYY-MM-DD"),
      },
    })
      .then((res) => {
        message.success("To-dos created successfully");
        setTodos((prev) => [...(res?.response || []), ...prev]);
        setChecklist([]);
        setNewItemText("");
        setOpen(false);
      })
      .catch(() => message.error("Failed to create to-dos"));
  };

  const toggleChecklistItem = (key) =>
    setChecklist((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, checked: !item.checked } : item
      )
    );

  const addChecklistItem = () => {
    if (!newItemText.trim()) return;
    setChecklist((prev) => [
      ...prev,
      { key: prev.length, label: newItemText.trim(), checked: false },
    ]);
    setNewItemText("");
  };

  const overviewCards = [
    { label: "Courses", value: overview?.total_courses ?? 0 },
    { label: "Assigned Courses", value: overview?.assigned_courses ?? 0 },
    { label: "Completion Rate", value: `${overview?.completion_rate ?? 0}%` },
    {
      label: "Average time taken/Unit",
      value: `${overview?.average_time_per_unit_hours ?? 0} hrs`,
    },
  ];

  return (
    <DashboardStyle>
      {initialLoader ? (
        <div className="dashboard_lazy_loading_container">
          {[...Array(4)].map((_, index) => (
            <div key={index}>
              <Skeleton.Node
                active={true}
                className="dashboard_lazy_loading"
                style={{
                  width: "35vw",
                  height: 300,
                  marginBottom: "16px",
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]} className="dashboard-row">
            <Col xs={24} xl={12}>
              <div className="overview-details-container1">
                <p className="details-box-title">
                  Overview of the Course created
                </p>
                <div className="overview-details-box">
                  {overviewCards.map((item, index) => (
                    <div key={index} className="overview-details">
                      <p className="overview-value">{item.value}</p>
                      <p className="overview-label">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
            <Col xs={24} xl={12}>
              <div className="overview-details-container1">
                <p className="details-box-title">Recent Activity</p>
                <div className="recent-activity-style">
                  {recentActivity.length === 0 ? (
                    <p className="activity-value">No recent activity yet</p>
                  ) : (
                    recentActivity.map((item, index) => (
                      <div
                        key={index}
                        className="activity-details"
                        style={item.course_id ? { cursor: "pointer" } : undefined}
                        onClick={() =>
                          item.course_id &&
                          navigate("/course", { state: { openCourseId: item.course_id } })
                        }
                      >
                        <p className="activity-label">{item.action_log}</p>
                        <p className="activity-value">
                          {item.user_name ? `By ${item.user_name} • ` : ""}
                          {formatActivityDate(item.action_date)}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </Col>
          </Row>

          <Row gutter={[16, 16]} className="dashboard-row">
            <Col xs={24} xl={12}>
              <div className="overview-details-container2">
                <div className="learner-progress-header">
                  <p className="details-box-title">Learner Progress</p>
                  <Select
                    className="select-dropdown-style"
                    placeholder="All Course"
                    getPopupContainer={(trigger) => trigger.parentNode}
                    value={selectedCourseId}
                    onChange={handleCourseFilterChange}
                    allowClear
                    onClear={() => handleCourseFilterChange(undefined)}
                  >
                    {courseOptions.map((course) => (
                      <Option key={course.course_id} value={course.course_id}>
                        {course.course_name}
                      </Option>
                    ))}
                  </Select>
                </div>

                <div className="overview-details-box">
                  <DonutChart
                    chartData={learnerProgress}
                    centerText={`${overview?.completion_rate ?? 0}%`}
                  />
                </div>
              </div>
            </Col>
            <Col xs={24} xl={12}>
              <div className="overview-details-container2">
                <div className="to-do-header">
                  <p className="details-box-title">My To-dos</p>
                  <button className="to-do-button" onClick={showModal}>
                    + Create
                  </button>
                </div>
                <div className="to-do-style">
                  {todos.length === 0 ? (
                    <p className="activity-value">No to-dos yet</p>
                  ) : (
                    todos.map((item) => {
                      const overdue =
                        !item.completed && dayjs(item.due_date).isBefore(dayjs(), "day");
                      return (
                        <div className="todo-content" key={item.task_id}>
                          <div>
                            <Checkbox
                              className="custom-checkbox"
                              checked={!!item.completed}
                              onChange={() => handleToggleComplete(item)}
                            >
                              <span className="todo-checkbox-text">{item.task_name}</span>
                            </Checkbox>
                          </div>
                          {/* Matches the design: every row shows its due date,
                              and hovering the row swaps it for edit/delete. */}
                          <div className="todo-right">
                            <div className={`todo-due${overdue ? " todo-due-overdue" : ""}`}>
                              <p className="todo-due-date">Due</p>
                              <p className="todo-due-date">
                                {formatShortOrdinalDate(item.due_date)}
                              </p>
                            </div>
                            <div className="todo-actions">
                              <span
                                className="todo-action-btn"
                                role="button"
                                tabIndex={0}
                                title="Edit"
                                aria-label="Edit to-do"
                                onClick={() => openEditTodo(item)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openEditTodo(item);
                                  }
                                }}
                              >
                                <EditPencil className="todo-action-icon" />
                              </span>
                              <span
                                className="todo-action-btn"
                                role="button"
                                tabIndex={0}
                                title="Delete"
                                aria-label="Delete to-do"
                                onClick={() => setDeleteTarget(item.task_id)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setDeleteTarget(item.task_id);
                                  }
                                }}
                              >
                                <TrashFull className="todo-action-icon" />
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </Col>
          </Row>

          <Modal
            title="Creating To-dos"
            open={open}
            onCancel={handleCancel}
            getContainer={false}
            centered
            width={632}
            className="todo-modal"
            footer={[
              <button
                key="cancel"
                className="to-do-button todo-modal-button todo-modal-button-outline"
                onClick={handleCancel}
              >
                Cancel
              </button>,
              <button
                key="create"
                className="to-do-button todo-modal-button"
                onClick={handleCreate}
              >
                Create
              </button>,
            ]}
          >
            {/* The design shows one bordered box of checkbox lines; the last
                line is where the next to-do is typed (Enter adds another). */}
            <div className="create-todo-checklist">
              {checklist.map((item) => (
                <div className="create-todo-checklist-item" key={item.key}>
                  <Checkbox
                    className="todo-modal-checkbox"
                    checked={item.checked}
                    onChange={() => toggleChecklistItem(item.key)}
                  >
                    <span className="todo-modal-text">{item.label}</span>
                  </Checkbox>
                </div>
              ))}
              <div className="create-todo-checklist-item">
                <Checkbox className="todo-modal-checkbox" disabled />
                <Input
                  variant="borderless"
                  className="create-todo-inline-input"
                  placeholder="Add a to-do and press Enter"
                  value={newItemText}
                  onChange={(event) => setNewItemText(event.target.value)}
                  onPressEnter={addChecklistItem}
                  autoFocus
                />
              </div>
            </div>
            <span className="create-todo-date-label">Due date to complete</span>
            <DatePicker
              className="create-todo-date-picker"
              value={dueDate}
              onChange={(date) => date && setDueDate(date)}
              format={formatOrdinalDate}
              allowClear={false}
            />
          </Modal>

          <Modal
            title="Editing To-dos"
            open={!!editingTodo}
            onCancel={closeEditTodo}
            getContainer={false}
            centered
            width={632}
            className="todo-modal"
            footer={[
              <button
                key="cancel"
                className="to-do-button todo-modal-button todo-modal-button-outline"
                onClick={closeEditTodo}
              >
                Cancel
              </button>,
              <button
                key="save"
                className="to-do-button todo-modal-button"
                onClick={handleSaveEditTodo}
              >
                Save
              </button>,
            ]}
          >
            <div className="create-todo-checklist">
              <div className="create-todo-checklist-item">
                <Checkbox
                  className="todo-modal-checkbox"
                  checked={!!editingTodo?.completed}
                  disabled
                />
                <Input
                  variant="borderless"
                  className="create-todo-inline-input"
                  placeholder="To-do"
                  value={editText}
                  onChange={(event) => setEditText(event.target.value)}
                  onPressEnter={handleSaveEditTodo}
                />
              </div>
            </div>
            <span className="create-todo-date-label">Due date to complete</span>
            <DatePicker
              className="create-todo-date-picker"
              value={editDueDate}
              onChange={(date) => date && setEditDueDate(date)}
              format={formatOrdinalDate}
              allowClear={false}
            />
          </Modal>

          <Modal
            open={!!deleteTarget}
            onCancel={() => setDeleteTarget(null)}
            getContainer={false}
            closable={false}
            centered
            width={460}
            className="todo-delete-modal"
            footer={[
              <button
                key="no"
                className="to-do-button todo-modal-button todo-modal-button-outline"
                onClick={() => setDeleteTarget(null)}
              >
                No
              </button>,
              <button
                key="yes"
                className="to-do-button todo-modal-button"
                onClick={confirmDeleteTodo}
              >
                Yes
              </button>,
            ]}
          >
            <p className="todo-delete-text">
              Are you sure you want to delete this To-do list?
            </p>
          </Modal>

          {/* "Deleted" confirmation from the design - closes by itself. */}
          <Modal
            open={showDeleted}
            onCancel={() => setShowDeleted(false)}
            getContainer={false}
            closable={false}
            centered
            width={194}
            footer={null}
            className="todo-deleted-modal"
          >
            <div className="todo-deleted-content">
              <span className="todo-deleted-icon">
                <TrashFull />
                <CheckCircleFilled className="todo-deleted-check" />
              </span>
              <p>Deleted</p>
            </div>
          </Modal>
        </>
      )}
    </DashboardStyle>
  );
};

export default Dashboard;
